import { useMutation, useQueryClient } from '@tanstack/react-query';

import { memberService } from '@/services/member-service';
import { pollForResourceGone } from '@/utils/poll';
import type { UUID } from '@/utils/uuid';

import { memberMutationKeys, memberQueryKeys } from './query-keys';

/**
 * Hook to remove a member from a tenant.
 * Waits until the read model no longer shows the member, then invalidates the cache.
 */
export function useRemoveMember(tenantId?: UUID) {
  const qc = useQueryClient();

  return useMutation({
    mutationKey: memberMutationKeys.removeMember(tenantId),
    mutationFn: async (userId: UUID) => {
      if (!tenantId) throw new Error('Missing tenant id');

      await memberService.removeMember(tenantId, userId);
      await pollForResourceGone(() =>
        memberService.findMember(tenantId, userId)
      );
      return userId;
    },
    onSuccess: async () => {
      await qc.invalidateQueries({
        queryKey: memberQueryKeys.listByTenant(tenantId),
        refetchType: 'all',
      });
    },
  });
}
